import * as axios from '../../axios-extended'
import * as t from 'io-ts'
import { task } from 'fp-ts'
import { withFallback } from 'io-ts-types/lib/withFallback'

const apiUrl = '/prosperbill/api'

const Order = t.type(
  {
    id: t.number,
    errorCode: withFallback(t.string, ''),
    errorMessage: withFallback(t.string, ''),
    formUrl: t.string,
  },
  'Order'
)

export type Order = t.TypeOf<typeof Order>

const SMTResponse = t.type({
  gateway: t.literal('SMT'),
  data: t.union([
    t.type({
      orderId: t.string,
      formUrl: t.string,
    }),
    t.type({
      errorCode: t.string,
      errorMessage: t.string,
    }),
  ]),
})

const MPGSResponse = t.type({
  gateway: t.literal('MPGS'),
  data: t.type({
    successIndicator: t.string,
    result: t.string,
    merchant: t.string,
    session: t.type({
      id: t.string,
      version: t.string,
    }),
    amount: t.number,
  }),
})

const PaypalResponse = t.type({
  gateway: t.literal('PAYPAL'),
  data: t.type({
    status: t.string,
    url: t.string,
    orderID: t.string,
  }),
})

const SobflousResponse = t.type({
  gateway: t.literal('SOBFLOUS'),
  data: t.type({
    orderID: t.string,
    url: t.string,
  }),
})

const PaymeeResponse = t.type({
  gateway: t.literal('PAYMEE'),
  data: t.type({
    token: t.string,
    amount: t.number,
  }),
})

export const CreatedOrder = t.type(
  {
    order: Order,
    additionalData: t.union([
      SMTResponse,
      MPGSResponse,
      PaypalResponse,
      SobflousResponse,
      PaymeeResponse,
    ]),
  },
  'CreatedOrder'
)

export const createPayment = (
  method: PaymentGateway,
  invoiceID: number,
  currency: string,
  cancelToken?: axios.CancelToken
) =>
  axios.post(
    `${apiUrl}/payment/${method}/create`,
    {
      invoiceID,
      currency,
      returnURL: `${window.location.href}/complete`,
    },
    { cancelToken, decoder: CreatedOrder }
  )

const PaymentResult = t.type({
  urlOk: withFallback(t.string as t.Type<string | undefined>, undefined),
  urlFailed: withFallback(t.string as t.Type<string | undefined>, undefined),
  status: t.keyof(
    {
      AUTHORIZED: null,
      FAILED: null,
    },
    'PaymentResult'
  ),
})

export type APIPaymentResult = t.TypeOf<typeof PaymentResult>

export const checkPayment = (
  paymentId: string,
  cancelToken?: axios.CancelToken
) =>
  axios.get(`${apiUrl}/payment/status/${paymentId}`, {
    cancelToken,
    decoder: PaymentResult,
  })

export const checkPaymentByOrderID = (
  orderId: string,
  cancelToken?: axios.CancelToken
) =>
  axios.get(`${apiUrl}/payment/order/${orderId}/status`, {
    cancelToken,
    decoder: PaymentResult,
  })

export const checkPaymentID = (
  orderId: string,
  cancelToken?: axios.CancelToken
) =>
  axios.get(`${apiUrl}/payment/check`, {
    cancelToken,
    decoder: PaymentResult,
    params: { orderId },
  })

type MPGSCheckoutConfiguration = {
  session: { id: string; version?: string }
  interaction: {
    locale?: string
    merchant: {
      email?: string
      logo?: string
      name: string
      phone?: string
      url?: string
    }
    operation: 'PURCHASE' | 'AUTHORIZE' | 'NONE' | 'VERIFY'
  }
  merchant: string
  order: {
    id?: string
    amount?: number
    description?: string
    currency: string
  }
}

type MPGSCheckout = {
  configure: (_: MPGSCheckoutConfiguration) => void
  showPaymentPage: () => void
  showLightbox: () => void
}

const checkoutUrl =
  process.env.MPGS_URL ??
  'https://test-tnpost.mtf.gateway.mastercard.com/checkout/version/57/checkout.js'

export const submitPaymeePayment = (invoiceID: number, token: string) => {
  const form = document.createElement('form')
  form.method = 'POST'
  form.action = `https://${
    process.env.PAYMEE_HOST ?? 'sandbox.paymee.tn'
  }/gateway/`

  const tokenInput = document.createElement('input')
  tokenInput.type = 'hidden'
  tokenInput.name = 'payment_token'
  tokenInput.value = token

  const urlOk = document.createElement('input')
  urlOk.type = 'hidden'
  urlOk.name = 'url_ok'
  urlOk.value = `https://${window.location.host}/prosper-bill/payment/${invoiceID}/complete`
  const urlKo = document.createElement('input')
  urlKo.type = 'hidden'
  urlKo.name = 'url_ko'
  urlKo.value = ''

  form.appendChild(tokenInput)
  form.appendChild(urlOk)
  form.appendChild(urlKo)

  document.body.appendChild(form)

  form.submit()
}

export const withMPGSCheckout = <T>(
  completeRedirect: string,
  fn: (_checkout: MPGSCheckout) => T
): task.Task<T> => {
  const script = document.createElement('script')

  // TODO: add callbacks somehow?
  script.dataset['complete'] = completeRedirect
  script.src = checkoutUrl
  script.async = true

  document.body.appendChild(script)
  return () =>
    new Promise<MPGSCheckout>(res => {
      script.addEventListener('load', () => {
        // @ts-ignore
        res(Checkout)
      })
    })
      .then(fn)
      .finally(() => {
        document.body.removeChild(script)
      })
}

export const PaymentGateway = t.keyof({
  SMT: null,
  MPGS: null,
  PAYPAL: null,
  SOBFLOUS: null,
  PAYMEE: null,
  DIRECT: null,
})
export type PaymentGateway = t.TypeOf<typeof PaymentGateway>

export const ExchangeRate = t.type({
  denominator: t.number,
  numerator: t.number,
  currency: t.string,
})
export type ExchangeRate = t.TypeOf<typeof ExchangeRate>

export const getAssetsByPaymentMethod = (
  unitID: string,
  cancelToken?: axios.CancelToken
) =>
  axios.get(`${apiUrl}/payment/supported-assets/${unitID}`, {
    cancelToken,
    decoder: t.type({
      SMT: withFallback(t.array(ExchangeRate), []),
      MPGS: withFallback(t.array(ExchangeRate), []),
      PAYPAL: withFallback(t.array(ExchangeRate), []),
      SOBFLOUS: withFallback(t.array(ExchangeRate), []),
      PAYMEE: withFallback(t.array(ExchangeRate), []),
      DIRECT: withFallback(t.array(ExchangeRate), []),
    }),
  })

export const handleMPGSRedirect = (
  formUrl: string,
  organizationName: string,
  merchant: string,
  o: {
    amount: number
    currency: string
    description: string
    id: string
  }
) =>
  withMPGSCheckout(
    `${window.location.pathname}/complete?id=${o.id}`,
    checkout => {
      const u = new URLSearchParams(formUrl)

      checkout.configure({
        merchant,
        order: o,
        session: {
          id: u.get('session.id') ?? '',
          version: u.get('session.version') ?? '',
        },
        interaction: {
          // TODO: maybe set locale here
          merchant: { name: organizationName },
          operation: 'PURCHASE',
        },
      })
      // checkout.showPaymentPage()
      checkout.showLightbox()
      return o
    }
  )
